/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Skeleton } from "@mui/material";
import { formatNumber } from "../../utils/numberFormatter";
import {
  GuestOrderVisitsCard,
  GuestOrderVisitsDivider,
  GuestOrderVisitsEmptyState,
  GuestOrderVisitsErrorState,
  GuestOrderVisitsHeader,
  GuestOrderVisitsLoadingWrap,
  GuestOrderVisitsMetricsRow,
  GuestOrderVisitsPrimaryGrid,
  GuestOrderVisitsRetryButton,
  GuestOrderVisitsSection,
  GuestOrderVisitsSingleMetric,
  GuestOrderVisitsSubtitle,
  GuestOrderVisitsTitle,
  GuestOrderVisitsValue,
  GuestOrderVisitsValueItem,
  GuestOrderVisitsValueLabel,
} from "./styles";

const formatCurrency = (value) => `$${formatNumber(value)}`;
const formatCount = (value) => formatNumber(value).replace(/\.00$/, '');
const formatRate = (value) => `${formatNumber(value)}%`;

const LoadingState = () => (
  <GuestOrderVisitsLoadingWrap data-testid="guest-order-visits-loader">
    <Skeleton variant="text" width="45%" height={28} />
    <Skeleton variant="rectangular" width="100%" height={88} sx={{ borderRadius: '12px' }} />
    <Skeleton variant="rectangular" width="100%" height={164} sx={{ borderRadius: '12px' }} />
    <Skeleton variant="rectangular" width="100%" height={88} sx={{ borderRadius: '12px' }} />
  </GuestOrderVisitsLoadingWrap>
);

export const GuestOrderVisits = ({ orderAndGuestData, isLoading, isError, onRetry }) => {
  if (isLoading) {
    return (
      <GuestOrderVisitsCard data-testid="guest-order-visits-card">
        <LoadingState />
      </GuestOrderVisitsCard>
    );
  }

  if (isError) {
    return (
      <GuestOrderVisitsCard data-testid="guest-order-visits-card">
        <GuestOrderVisitsHeader>
          <GuestOrderVisitsTitle>NEED TO KNOW</GuestOrderVisitsTitle>
        </GuestOrderVisitsHeader>
        <GuestOrderVisitsErrorState data-testid="guest-order-visits-error-state">
          Failed to load order and visit data.
          <GuestOrderVisitsRetryButton
            variant="outlined"
            onClick={onRetry}
            data-testid="guest-order-visits-retry-button"
          >
            Retry
          </GuestOrderVisitsRetryButton>
        </GuestOrderVisitsErrorState>
      </GuestOrderVisitsCard>
    );
  }

  if (!orderAndGuestData) {
    return (
      <GuestOrderVisitsCard data-testid="guest-order-visits-card">
        <GuestOrderVisitsHeader>
          <GuestOrderVisitsTitle>NEED TO KNOW</GuestOrderVisitsTitle>
        </GuestOrderVisitsHeader>
        <GuestOrderVisitsEmptyState data-testid="guest-order-visits-empty-state">
          No order and visit data found.
        </GuestOrderVisitsEmptyState>
      </GuestOrderVisitsCard>
    );
  }

  return (
    <GuestOrderVisitsCard data-testid="guest-order-visits-card">
      <GuestOrderVisitsHeader>
        <GuestOrderVisitsTitle>NEED TO KNOW</GuestOrderVisitsTitle>
      </GuestOrderVisitsHeader>

      <GuestOrderVisitsSection>
        <GuestOrderVisitsSubtitle>VISITS + ORDERS</GuestOrderVisitsSubtitle>
        <GuestOrderVisitsPrimaryGrid>
          <GuestOrderVisitsValueItem data-testid="guest-order-visits-total-net-spend">
            <GuestOrderVisitsValue>{formatCurrency(orderAndGuestData?.total_net_spend)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Total Net Spend</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>

          <GuestOrderVisitsValueItem data-testid="guest-order-visits-total-visits">
            <GuestOrderVisitsValue>{formatCount(orderAndGuestData?.total_visits)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Visits + Orders</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>
        </GuestOrderVisitsPrimaryGrid>
      </GuestOrderVisitsSection>

      <GuestOrderVisitsDivider />

      <GuestOrderVisitsSection>
        <GuestOrderVisitsSubtitle>VISITS</GuestOrderVisitsSubtitle>
        <GuestOrderVisitsSingleMetric data-testid="guest-order-visits-average-net-spend">
          <GuestOrderVisitsValue>{formatCurrency(orderAndGuestData?.average_net_spend)}</GuestOrderVisitsValue>
          <GuestOrderVisitsValueLabel>Avg Per Count</GuestOrderVisitsValueLabel>
        </GuestOrderVisitsSingleMetric>

        <GuestOrderVisitsMetricsRow columns={2}>
          <GuestOrderVisitsValueItem data-testid="guest-order-visits-total-gross">
            <GuestOrderVisitsValue>{formatCurrency(orderAndGuestData?.total_gross)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Total Gross Spend</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>

          <GuestOrderVisitsValueItem data-testid="guest-order-visits-total-tip">
            <GuestOrderVisitsValue>{formatCurrency(orderAndGuestData?.total_tip)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Total Tip</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>
        </GuestOrderVisitsMetricsRow>

        <GuestOrderVisitsMetricsRow columns={3}>
          <GuestOrderVisitsValueItem data-testid="guest-order-visits-named-guest-visits">
            <GuestOrderVisitsValue>{formatCount(orderAndGuestData?.named_guest_visit_count)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Named Guest Visits</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>

          <GuestOrderVisitsValueItem data-testid="guest-order-visits-check-in-visits">
            <GuestOrderVisitsValue>{formatCount(orderAndGuestData?.check_in_visit_count)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Check-ins Visits</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>

          <GuestOrderVisitsValueItem data-testid="guest-order-visits-rate">
            <GuestOrderVisitsValue>{formatRate(orderAndGuestData?.rate)}</GuestOrderVisitsValue>
            <GuestOrderVisitsValueLabel>Rate</GuestOrderVisitsValueLabel>
          </GuestOrderVisitsValueItem>
        </GuestOrderVisitsMetricsRow>
      </GuestOrderVisitsSection>

      <GuestOrderVisitsDivider />

      <GuestOrderVisitsSection>
        <GuestOrderVisitsSubtitle>ORDERS</GuestOrderVisitsSubtitle>
        <GuestOrderVisitsSingleMetric data-testid="guest-order-visits-total-discount">
          <GuestOrderVisitsValue>{formatCurrency(orderAndGuestData?.total_discount)}</GuestOrderVisitsValue>
          <GuestOrderVisitsValueLabel>Total Discount</GuestOrderVisitsValueLabel>
        </GuestOrderVisitsSingleMetric>
      </GuestOrderVisitsSection>
    </GuestOrderVisitsCard>
  );
};
